/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { Badge, Checkbox, CollapseSection } from '@/components/ui'
import { CATALOGUE } from '@/insights/insightCatalogue'

import { isInsightCapped } from './capRules'
import { InsightRow } from './InsightRow'
import type { PermissionsCap } from './types'
import type { UsageCount, UsageIndex } from './usage'

interface Props {
  insights: string[]
  onChange: (insights: string[]) => void
  cap: PermissionsCap | null
  usage: UsageIndex
  inUseLabel: (count: UsageCount) => string
  disabled?: boolean
}

/**
 * One collapsible per category. The category checkbox reflects and drives only the
 * insights the company allows; capped ones stay off whatever the category says.
 */
export const InsightCategoriesSection: React.FC<Props> = ({ insights, onChange, cap, usage, inUseLabel, disabled }) => {
  const setChecked = (slugs: string[], checked: boolean) => {
    const without = insights.filter((slug) => !slugs.includes(slug))
    onChange(checked ? [...without, ...slugs] : without)
  }

  return (
    <div className="mt-4" data-testid="insight-categories">
      <p className="mb-1 text-sm font-medium">Insights</p>
      <div className="flex flex-col gap-2">
        {CATALOGUE.map((category) => {
          const selectable = category.insights.map((i) => i.slug).filter((slug) => !isInsightCapped(cap, slug))
          const checkedCount = selectable.filter((slug) => insights.includes(slug)).length
          const all = selectable.length > 0 && checkedCount === selectable.length
          const some = checkedCount > 0 && !all
          // Distinct users and dashboards across the category, so one dashboard counts once.
          const used = usage.forCategory(category.insights.map((i) => i.slug))
          return (
            <CollapseSection
              key={category.key}
              data-testid={`category-section-${category.key}`}
              title={
                <Checkbox
                  id={`category-${category.key}`}
                  data-testid={`category-${category.key}`}
                  label={<span className="font-medium">{category.name}</span>}
                  checked={all}
                  indeterminate={some}
                  disabled={disabled || selectable.length === 0}
                  onChange={(checked) => setChecked(selectable, checked)}
                  trailing={
                    <>
                      <Badge variant="ghost" size="xs">
                        {checkedCount}/{category.insights.length}
                      </Badge>
                      {used.users > 0 && (
                        <Badge variant="info" size="xs">
                          {inUseLabel(used)}
                        </Badge>
                      )}
                    </>
                  }
                />
              }
            >
              <div className="flex flex-col gap-2 pl-6">
                {category.insights.map((insight) => (
                  <InsightRow
                    key={insight.slug}
                    insight={insight}
                    checked={insights.includes(insight.slug)}
                    onChange={(checked) => setChecked([insight.slug], checked)}
                    cap={cap}
                    used={usage.forSlug(insight.slug)}
                    inUseLabel={inUseLabel}
                    disabled={disabled}
                  />
                ))}
              </div>
            </CollapseSection>
          )
        })}
      </div>
    </div>
  )
}

export default InsightCategoriesSection
